/**
 * Référentiel de labels PROPRE au gîte (meublé de tourisme, art. L324-1).
 *
 * Volontairement DISJOINT de `tourism-labels.ts` (chambres d'hôtes) : le gîte
 * relève d'un référentiel différent, et surtout le classement étoiles Atout
 * France (`AtoutFranceMeuble`) ne s'applique QU'aux meublés de tourisme — le
 * réutiliser côté chambres émettrait un credential Schema.org incorrect
 * (plan §344 : risque de rejet de rich result, pas une imprécision éditoriale).
 *
 * Aucun import croisé avec `tourism-labels.ts` : le découplage est le point du
 * lot. `GitesDeFrame`/`Clevacances` sont redéclarés ici avec leurs sous-niveaux,
 * et `AccueilPaysan` est volontairement dupliqué depuis `tourism-labels.ts`
 * (même certifieur, URL, type Schema.org et logo — verrouillé par
 * `tourism-labels.spec.ts`).
 *
 * Deux sortes de labels :
 * - à niveaux (`sublevelConfig` : épis, clés, étoiles) → menu déroulant de
 *   niveau dans le sélecteur (`GITE_TIERED_TOURISM_LABEL_KEYS`) ;
 * - simples, sans niveau (réseau à charte, ex. Accueil Paysan) → bouton toggle
 *   sous « Réseau d'accueil » (`GITE_SIMPLE_TOURISM_LABEL_KEYS`).
 *
 * Les logos sont dans public/images/labels/<key>.<ext>, formats résolus via
 * `logoFormats` (défaut svg → webp → png ; fallback icône si absent).
 */

export const GITE_TOURISM_LABEL_KEYS = [
  'GitesDeFrame',
  'Clevacances',
  'AtoutFranceMeuble',
  'AccueilPaysan',
] as const

export type GiteTourismLabelKey = (typeof GITE_TOURISM_LABEL_KEYS)[number]

/** Définition d'un sous-niveau (épi, clé, étoile). */
export interface GiteTourismLabelSublevel {
  value: number
  label: string
  /** Libellé anglais du niveau (4.144.1), affiché sur la fiche `/en`. */
  labelEn: string
}

/** Langue d'affichage du référentiel (4.144.1). */
export type GiteTourismLabelLocale = 'fr' | 'en'

export interface GiteTourismLabelConfig {
  key: GiteTourismLabelKey
  /** Nom court affiché sur le badge */
  name: string
  /** Nom anglais (4.144.1) — identique au FR pour les noms propres de réseau. */
  nameEn: string
  /** Description affichée dans le tooltip */
  description: string
  /** Description anglaise (4.144.1). */
  descriptionEn: string
  /** Organisme certificateur */
  certifier: string
  /** URL vers le site de l'organisme */
  certifierUrl?: string
  /** Type Schema.org à utiliser pour ce label */
  schemaOrgType: 'award' | 'hasCredential'
  /** Formats de logo disponibles, par ordre de priorité. Défaut ['svg', 'webp', 'png']. */
  logoFormats?: Array<'svg' | 'webp' | 'png'>
  /** Sous-niveaux (le sélecteur affiche un menu déroulant de niveau). */
  sublevelConfig?: {
    symbol: string
    levels: GiteTourismLabelSublevel[]
  }
}

/**
 * Configuration complète des labels gîte.
 */
export const GITE_TOURISM_LABELS: Record<GiteTourismLabelKey, GiteTourismLabelConfig> = {
  GitesDeFrame: {
    key: 'GitesDeFrame',
    name: 'Gîtes de France',
    nameEn: 'Gîtes de France',
    descriptionEn: 'Quality label of the Gîtes de France network, guaranteeing comfort and authenticity, rated from 1 to 4 ears of wheat.',
    description: 'Label qualité du réseau Gîtes de France, garantissant confort et authenticité, classé de 1 à 4 épis.',
    certifier: 'Gîtes de France',
    certifierUrl: 'https://www.gites-de-france.com',
    schemaOrgType: 'hasCredential',
    sublevelConfig: {
      symbol: 'épi',
      levels: [
        { value: 1, label: '1 épi', labelEn: '1 ear of wheat' },
        { value: 2, label: '2 épis', labelEn: '2 ears of wheat' },
        { value: 3, label: '3 épis', labelEn: '3 ears of wheat' },
        { value: 4, label: '4 épis', labelEn: '4 ears of wheat' },
      ],
    },
  },
  Clevacances: {
    key: 'Clevacances',
    name: 'Clévacances',
    nameEn: 'Clévacances',
    descriptionEn: 'National quality label for holiday rentals, rated from 1 to 5 keys.',
    description: 'Label national de qualité pour les locations de vacances, classé de 1 à 5 clés.',
    certifier: 'Clévacances',
    certifierUrl: 'https://www.clevacances.com',
    schemaOrgType: 'hasCredential',
    sublevelConfig: {
      symbol: 'clé',
      levels: [
        { value: 1, label: '1 clé', labelEn: '1 key' },
        { value: 2, label: '2 clés', labelEn: '2 keys' },
        { value: 3, label: '3 clés', labelEn: '3 keys' },
        { value: 4, label: '4 clés', labelEn: '4 keys' },
        { value: 5, label: '5 clés', labelEn: '5 keys' },
      ],
    },
  },
  AtoutFranceMeuble: {
    key: 'AtoutFranceMeuble',
    name: 'Meublé de tourisme classé',
    nameEn: 'Classified furnished tourist accommodation',
    descriptionEn: 'Official rating of furnished tourist accommodation in France (art. L324-1), issued by Atout France, from 1 to 5 stars according to comfort and amenities. Does not apply to bed and breakfasts.',
    description: 'Classement officiel des meublés de tourisme (art. L324-1), délivré par Atout France, de 1 à 5 étoiles selon le confort et les équipements. Ne s\'applique pas aux chambres d\'hôtes.',
    certifier: 'Atout France',
    certifierUrl: 'https://www.classement.atout-france.fr',
    schemaOrgType: 'hasCredential',
    logoFormats: [],
    sublevelConfig: {
      symbol: 'étoile',
      levels: [
        { value: 1, label: '1 étoile', labelEn: '1 star' },
        { value: 2, label: '2 étoiles', labelEn: '2 stars' },
        { value: 3, label: '3 étoiles', labelEn: '3 stars' },
        { value: 4, label: '4 étoiles', labelEn: '4 stars' },
        { value: 5, label: '5 étoiles', labelEn: '5 stars' },
      ],
    },
  },
  // Label simple (adhésion à une charte, pas de classement) : aucun
  // `sublevelConfig`, le sélecteur le rend en toggle sous « Réseau d'accueil ».
  // À ne pas confondre avec le classement des meublés (`AtoutFranceMeuble`),
  // qu'Accueil Paysan délivre aussi en tant qu'organisme agréé.
  AccueilPaysan: {
    key: 'AccueilPaysan',
    name: 'Accueil Paysan',
    nameEn: 'Accueil Paysan',
    descriptionEn: 'Network of farmers who welcome guests in farm gîtes: commitment to an ethical charter of sustainable farming, warm hospitality and sharing farm life.',
    description: 'Réseau de paysans qui accueillent en gîtes paysans sur leur ferme : adhésion à une charte éthique d\'agriculture durable, d\'accueil chaleureux et de partage de la vie paysanne.',
    certifier: 'Fédération nationale Accueil Paysan',
    certifierUrl: 'https://www.accueil-paysan.com/fr/',
    schemaOrgType: 'award',
    logoFormats: ['png'],
  },
}

/** Labels à niveaux (épis, clés, étoiles) : rendus en menu déroulant de niveau. */
export const GITE_TIERED_TOURISM_LABEL_KEYS: GiteTourismLabelKey[] = GITE_TOURISM_LABEL_KEYS
  .filter(key => GITE_TOURISM_LABELS[key].sublevelConfig)

/** Labels simples (réseau à charte, sans niveau) : rendus en bouton toggle. */
export const GITE_SIMPLE_TOURISM_LABEL_KEYS: GiteTourismLabelKey[] = GITE_TOURISM_LABEL_KEYS
  .filter(key => !GITE_TOURISM_LABELS[key].sublevelConfig)

/**
 * Retourne la config d'un label gîte. undefined si clé invalide.
 */
export function getGiteTourismLabelConfig(key: string): GiteTourismLabelConfig | undefined {
  return GITE_TOURISM_LABELS[key as GiteTourismLabelKey]
}

/**
 * Vérifie qu'une clé est un label gîte valide.
 */
export function isValidGiteTourismLabelKey(key: string): key is GiteTourismLabelKey {
  return GITE_TOURISM_LABEL_KEYS.includes(key as GiteTourismLabelKey)
}

/**
 * Chemins candidats vers le logo d'un label gîte, par ordre de priorité.
 */
export function getGiteTourismLabelLogoCandidates(key: GiteTourismLabelKey): string[] {
  const formats = GITE_TOURISM_LABELS[key].logoFormats ?? ['svg', 'webp', 'png']
  return formats.map(ext => `/images/labels/${key}.${ext}`)
}

/**
 * Libellé du sous-niveau pour un label gîte donné.
 * Ex: formatGiteSublevel('GitesDeFrame', 3) → "3 épis"
 */
export function formatGiteSublevel(key: string, level: number, locale: GiteTourismLabelLocale = 'fr'): string {
  const config = getGiteTourismLabelConfig(key)
  if (!config?.sublevelConfig) return String(level)
  const found = config.sublevelConfig.levels.find(l => l.value === level)
  if (!found) return String(level)
  return locale === 'en' ? found.labelEn : found.label
}

/** Nom du label dans la langue demandée (4.144.1). */
export function getGiteTourismLabelName(config: GiteTourismLabelConfig, locale: GiteTourismLabelLocale = 'fr'): string {
  return locale === 'en' ? config.nameEn : config.name
}

/** Description du label dans la langue demandée (4.144.1). */
export function getGiteTourismLabelDescription(config: GiteTourismLabelConfig, locale: GiteTourismLabelLocale = 'fr'): string {
  return locale === 'en' ? config.descriptionEn : config.description
}

/**
 * Icône Lucide par symbole de sous-niveau. Centralisée ici et non dans chaque
 * consommateur : le sélecteur propriétaire et le badge public en tenaient
 * chacun une copie, et un symbole ajouté au référentiel sans icône retombait
 * silencieusement sur un repli — la même régression muette que ce référentiel
 * a déjà produite sur l'affichage des labels (fix 4.100.1).
 *
 * `app/components/HoliGiteTourismLabels.spec.ts` verrouille la couverture :
 * tout `sublevelConfig.symbol` doit avoir son entrée ici.
 */
export const GITE_SUBLEVEL_SYMBOL_ICONS: Record<string, string> = {
  'épi': 'i-lucide-wheat',
  'clé': 'i-lucide-key-round',
  'étoile': 'i-lucide-star',
}

/** Icône du symbole, ou `undefined` si le symbole n'en a pas — jamais un repli muet. */
export function getGiteSublevelIcon(symbol: string): string | undefined {
  return GITE_SUBLEVEL_SYMBOL_ICONS[symbol]
}
